import { z } from 'zod';
import {
  IdSchema,
  JsonValueSchema,
  NonEmptyStringSchema,
  TimestampSchema,
  type JsonValue,
} from './common';
import {
  BlockTypeEnum,
  ExperienceTypeEnum,
  FunnelTypeEnum,
  LayoutModeEnum,
  StepTransitionEventEnum,
  StepTypeEnum,
  TrafficTypeEnum,
} from '../taxonomy';
import { AwarenessStageEnum, BrandPageContextSchema } from './framework';
import { ManagedSemanticBehaviorSchema } from './managed-behavior';
import { PageBlockLayoutSchema } from './page-content';
import { StepListenerConfigSchema } from './structure';
import {
  KnownBlockEventsSchema,
  KnownTssFunnelEventNameSchema,
} from './ui-events';

/**
 * Funnel Blueprint contract.
 *
 * A Blueprint is a *brand-agnostic, portable* description of a funnel that has
 * proven to convert (usually reverse-engineered from a Funnel Analysis run). It
 * is a portable source model that can materialize Funnel Templates: it carries
 * the funnel structure AND the generation metadata a downstream generator (the
 * "vibe coder") needs to re-apply the same conversion patterns to a *different*
 * brand/product on the Storefront Layout surface. It is not wire-compatible
 * with TemplateSchema and consumers must perform an explicit apply step.
 *
 * Distinction from Template: a Template is authored by an operator and is
 * concrete. A Blueprint is system-suggested, replaces the blank-page starting
 * point, and its content is expressed as *slots* that get bound to a target
 * brand/product at apply-time.
 *
 * Casing: camelCase, to match the persisted API/entity contracts (api.ts,
 * TemplateSchema) since Blueprints are stored and CRUD'd by tss-svc-funnels.
 */

export const BLUEPRINT_STATUSES = ['draft', 'published', 'archived'] as const;

export const BLUEPRINT_SOURCE_KINDS = [
  'analysis',
  'authored',
  'derived_from_funnel',
] as const;

/**
 * What happens to the source brand's look when the blueprint is applied.
 * - adopt_target_brand: keep STRUCTURE + conversion patterns, take colors/fonts
 *   from the target brand (default — "copy what converts, stay on-brand").
 * - preserve_source_look: reproduce the source visual design (near-clone).
 */
export const BLUEPRINT_TOKEN_POLICIES = [
  'adopt_target_brand',
  'preserve_source_look',
] as const;

export const BLUEPRINT_VIEWPORTS = ['desktop', 'mobile'] as const;

/** The conversion role a component slot plays on a page. */
export const CONVERSION_INTENTS = [
  'hero',
  'value_proposition',
  'social_proof',
  'reviews',
  'offer',
  'urgency',
  'scarcity',
  'guarantee',
  'trust_badges',
  'cross_sell',
  'upsell',
  'free_gift',
  'lead_capture',
  'recommendations',
  'faq',
  'comparison',
  'cta',
  'navigation',
] as const;

/** Roles for asset slots the target brand fills at apply-time. */
export const BLUEPRINT_ASSET_SLOT_ROLES = [
  'logo',
  'hero_image',
  'hero_video',
  'product_image',
  'lifestyle_image',
  'background',
  'icon',
  'testimonial_avatar',
  'badge',
  'favicon',
] as const;

/**
 * Native Storefront Layout components a blueprint expects to be *wired and
 * configured* (never faked) rather than generated from scratch.
 */
export const BLUEPRINT_NATIVE_COMPONENTS = [
  'reviews',
  'recommendations',
  'packs',
  'side_cart',
  'country_selector',
  'free_gift',
  'announcement_bar',
  'newsletter',
  'search',
] as const;

export const BlueprintStatusEnum = z.enum(BLUEPRINT_STATUSES);
export const BlueprintSourceKindEnum = z.enum(BLUEPRINT_SOURCE_KINDS);
export const BlueprintTokenPolicyEnum = z.enum(BLUEPRINT_TOKEN_POLICIES);
export const BlueprintViewportEnum = z.enum(BLUEPRINT_VIEWPORTS);
export const ConversionIntentEnum = z.enum(CONVERSION_INTENTS);
export const BlueprintAssetSlotRoleEnum = z.enum(BLUEPRINT_ASSET_SLOT_ROLES);
export const BlueprintNativeComponentEnum = z.enum(BLUEPRINT_NATIVE_COMPONENTS);

/**
 * Portable design tokens (shaped after the funnel-analysis ThemeRecipe). These
 * capture the SOURCE look as a fallback; under the default `adopt_target_brand`
 * policy they are overridden by the target brand's tokens at apply-time.
 */
export const BlueprintDesignSystemSchema = z
  .object({
    colors: z.record(NonEmptyStringSchema, NonEmptyStringSchema).optional(),
    fonts: z.record(NonEmptyStringSchema, NonEmptyStringSchema).optional(),
    spacing: z
      .object({
        base: z.number().optional(),
        scale: z.array(z.number()).optional(),
        gridColumns: z.number().int().positive().optional(),
        gutterPx: z.number().nonnegative().optional(),
        maxContainerPx: z.number().positive().optional(),
      })
      .strict()
      .optional(),
    radius: z.record(NonEmptyStringSchema, JsonValueSchema).optional(),
    shadows: z.record(NonEmptyStringSchema, JsonValueSchema).optional(),
    breakpoints: z.record(NonEmptyStringSchema, JsonValueSchema).optional(),
    durations: z.record(NonEmptyStringSchema, JsonValueSchema).optional(),
  })
  .strict();

/** Provenance: where the blueprint came from. */
export const BlueprintProvenanceSchema = z
  .object({
    kind: BlueprintSourceKindEnum,
    analysisRunId: z.string().optional(),
    bundleId: z.string().optional(),
    sourceUrl: z.string().url().optional(),
    sourceBrand: z.string().optional(),
    sourceFunnelType: FunnelTypeEnum.optional(),
    matchConfidence: z.number().min(0).max(1).optional(),
    capturedAt: TimestampSchema.optional(),
  })
  .strict();

/** Where this blueprint sits in the funnel taxonomy. */
export const BlueprintClassificationSchema = z
  .object({
    funnelType: FunnelTypeEnum,
    frameworkType: z
      .object({
        id: z.number().int().min(1).max(7),
        label: NonEmptyStringSchema,
      })
      .strict()
      .optional(),
    trafficType: TrafficTypeEnum.optional(),
    awarenessStage: AwarenessStageEnum.optional(),
  })
  .strict();

/**
 * A single fill-in point on a component (copy, image, product ref, …). Carries
 * intent + an optional captured example from the source (guidance, NOT a value
 * to copy literally onto the target brand).
 */
export const BlueprintContentSlotSchema = z
  .object({
    key: NonEmptyStringSchema,
    kind: z.enum(['copy', 'image', 'video', 'product_ref', 'link', 'icon']),
    intent: NonEmptyStringSchema,
    sourceExample: z.string().optional(),
    required: z.boolean().default(true),
    maxLength: z.number().int().positive().optional(),
  })
  .strict();

/** Provenance for a component slot back to the analyzed source. */
export const BlueprintComponentEvidenceSchema = z
  .object({
    assetId: NonEmptyStringSchema.optional(),
    selector: NonEmptyStringSchema.optional(),
    screenshotCrop: NonEmptyStringSchema.optional(),
    note: NonEmptyStringSchema.optional(),
  })
  .strict();

/**
 * A portable component tree node. The fidelity fields intentionally mirror the
 * Storefront Layout persistence/runtime seam so applying a Blueprint cannot
 * silently flatten visual, advanced, responsive, nested, or behavioral state.
 */
export interface BlueprintComponentSlot {
  id: string;
  blockType: z.infer<typeof BlockTypeEnum>;
  conversionIntent: z.infer<typeof ConversionIntentEnum>;
  nativeComponent?: z.infer<typeof BlueprintNativeComponentEnum> | undefined;
  order: number;
  required: boolean;
  props: Record<string, JsonValue>;
  layout?: z.infer<typeof PageBlockLayoutSchema> | undefined;
  styles?: Record<string, JsonValue> | undefined;
  advanced?: Record<string, JsonValue> | undefined;
  responsiveOverrides?: Record<string, JsonValue> | undefined;
  contentSlots: z.infer<typeof BlueprintContentSlotSchema>[];
  events?: z.infer<typeof KnownBlockEventsSchema> | undefined;
  children: BlueprintComponentSlot[];
  sourceEvidence?:
    | z.infer<typeof BlueprintComponentEvidenceSchema>
    | undefined;
  notes?: string | undefined;
}

/**
 * One component on a page. Either generate it (blockType) or wire a native
 * store component (nativeComponent) — the latter is preferred for reviews,
 * recommendations, free gifts, etc. Children use the same strict shape, so
 * known TSS event bindings and component fidelity survive at every depth.
 */
export const BlueprintComponentSlotSchema: z.ZodType<
  BlueprintComponentSlot,
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
    z
      .object({
        id: NonEmptyStringSchema,
        blockType: BlockTypeEnum,
        conversionIntent: ConversionIntentEnum,
        nativeComponent: BlueprintNativeComponentEnum.optional(),
        order: z.number().int().nonnegative(),
        required: z.boolean().default(true),
        props: z.record(JsonValueSchema).default({}),
        layout: PageBlockLayoutSchema.optional(),
        styles: z.record(JsonValueSchema).optional(),
        advanced: z.record(JsonValueSchema).optional(),
        responsiveOverrides: z.record(JsonValueSchema).optional(),
        contentSlots: z.array(BlueprintContentSlotSchema).default([]),
        events: KnownBlockEventsSchema.optional(),
        children: z.array(BlueprintComponentSlotSchema).default([]),
        sourceEvidence: BlueprintComponentEvidenceSchema.optional(),
        notes: z.string().optional(),
      })
      .strict(),
  );

/** Brand-agnostic step transition (mirrors FunnelStructure StepTransition). */
export const BlueprintStepTransitionSchema = z
  .object({
    event: StepTransitionEventEnum,
    target: z.number().int().nonnegative(),
    priority: z.number().int().optional(),
  })
  .strict();

/**
 * Portable listener stored with a Blueprint step. Generated Blueprints may
 * subscribe only to TSS-owned behavioral or structural names, while retaining
 * the complete listener config/reaction shape used by persisted funnels.
 */
export const BlueprintStepListenerSchema = z
  .object({
    id: z.string().optional(),
    eventName: KnownTssFunnelEventNameSchema,
    config: StepListenerConfigSchema,
    managedBehavior: ManagedSemanticBehaviorSchema.optional(),
  })
  .strict();

/** One screen/step of the funnel. */
export const BlueprintStepSchema = z
  .object({
    id: NonEmptyStringSchema,
    position: z.number().int().nonnegative(),
    stepType: StepTypeEnum,
    experienceType: ExperienceTypeEnum,
    name: NonEmptyStringSchema,
    goal: NonEmptyStringSchema.optional(),
    layoutMode: LayoutModeEnum.optional(),
    components: z.array(BlueprintComponentSlotSchema).default([]),
    listeners: z.array(BlueprintStepListenerSchema),
    transitions: z.array(BlueprintStepTransitionSchema).default([]),
  })
  .strict();

/** An asset the target brand must supply, described (not embedded). */
export const BlueprintAssetSlotSchema = z
  .object({
    role: BlueprintAssetSlotRoleEnum,
    key: NonEmptyStringSchema,
    viewports: z
      .array(BlueprintViewportEnum)
      .min(1)
      .default([...BLUEPRINT_VIEWPORTS]),
    aspectRatio: z.string().optional(),
    minWidthPx: z.number().int().positive().optional(),
    required: z.boolean().default(true),
    sourceAssetId: z.string().optional(),
  })
  .strict();

/**
 * A proven conversion pattern to replicate, expressed brand-agnostically
 * (templated copy with placeholders + placement + evidence).
 */
export const BlueprintConversionPatternSchema = z
  .object({
    intent: ConversionIntentEnum,
    description: NonEmptyStringSchema,
    copyTemplate: z.string().optional(),
    placement: z.string().optional(),
    evidence: z.array(NonEmptyStringSchema).default([]),
  })
  .strict();

/** What the downstream generator needs to honor when applying the blueprint. */
export const BlueprintApplyContractSchema = z
  .object({
    targetSurface: z.literal('storefront-layout'),
    viewports: z
      .array(BlueprintViewportEnum)
      .min(1)
      .default([...BLUEPRINT_VIEWPORTS]),
    tokenPolicy: BlueprintTokenPolicyEnum.default('adopt_target_brand'),
    requiredBrandInputs: z.array(NonEmptyStringSchema).default([]),
    nativeAppsRequired: z.array(BlueprintNativeComponentEnum).default([]),
  })
  .strict();

interface PortableComponentGraphNode {
  id: string;
  children: readonly PortableComponentGraphNode[];
}

interface PortableStepGraphNode {
  id: string;
  position: number;
  components: readonly PortableComponentGraphNode[];
  listeners: readonly z.infer<typeof BlueprintStepListenerSchema>[];
  transitions: readonly z.infer<typeof BlueprintStepTransitionSchema>[];
}

function addPortableStepGraphIssues(
  steps: readonly PortableStepGraphNode[],
  ctx: z.RefinementCtx,
  rootPath: (string | number)[],
): void {
  const positions = new Set(steps.map((step) => step.position));
  const stepIds = new Set(steps.map((step) => step.id));

  if (positions.size !== steps.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...rootPath],
      message: 'Step positions must be unique',
    });
  }

  if (stepIds.size !== steps.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...rootPath],
      message: 'Step ids must be unique',
    });
  }

  const ordered = steps
    .map((step, index) => ({ step, index }))
    .sort((left, right) => left.step.position - right.step.position);

  ordered.forEach(({ step, index }, expectedPosition) => {
    if (step.position !== expectedPosition) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...rootPath, index, 'position'],
        message: 'Step positions must be sequential starting from 0',
      });
    }

    const componentIds: string[] = [];
    const collectComponentIds = (
      components: readonly PortableComponentGraphNode[],
    ): void => {
      components.forEach((component) => {
        componentIds.push(component.id);
        collectComponentIds(component.children);
      });
    };
    collectComponentIds(step.components);
    if (new Set(componentIds).size !== componentIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...rootPath, index, 'components'],
        message: 'Component ids must be unique across the entire step tree',
      });
    }

    step.transitions.forEach((transition, transitionIndex) => {
      if (!positions.has(transition.target)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [...rootPath, index, 'transitions', transitionIndex, 'target'],
          message: `Transition target ${transition.target} does not reference a step in this portable flow`,
        });
      }
    });

    step.listeners.forEach((listener, listenerIndex) => {
      const navigate = listener.config.reaction?.navigate;
      if (
        listener.config.reaction?.kind === 'navigate' &&
        navigate?.type === 'go_to_step' &&
        navigate.stepId !== undefined &&
        !stepIds.has(navigate.stepId)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [
            ...rootPath,
            index,
            'listeners',
            listenerIndex,
            'config',
            'reaction',
            'navigate',
            'stepId',
          ],
          message: `Listener target "${navigate.stepId}" does not reference a step in this portable flow`,
        });
      }
    });
  });
}

/**
 * The Funnel Blueprint — the persisted, portable, brand-agnostic funnel model.
 */
export const FunnelBlueprintSchema = z
  .object({
    id: IdSchema,
    tenantId: IdSchema,
    storeId: z.string().nullable().optional(),
    name: NonEmptyStringSchema,
    description: z.string().nullable().optional(),
    status: BlueprintStatusEnum.default('draft'),
    version: z.number().int().nonnegative().default(0),
    tags: z.array(NonEmptyStringSchema).default([]),
    provenance: BlueprintProvenanceSchema,
    classification: BlueprintClassificationSchema,
    flow: z
      .object({
        steps: z.array(BlueprintStepSchema).min(1),
      })
      .strict(),
    designSystem: BlueprintDesignSystemSchema.optional(),
    assetSlots: z.array(BlueprintAssetSlotSchema).default([]),
    conversionKit: z.array(BlueprintConversionPatternSchema).default([]),
    applyContract: BlueprintApplyContractSchema,
    createdAt: TimestampSchema.optional(),
    updatedAt: TimestampSchema.optional(),
    deletedAt: TimestampSchema.nullable().optional(),
  })
  .strict()
  .superRefine((blueprint, ctx) => {
    addPortableStepGraphIssues(blueprint.flow.steps, ctx, ['flow', 'steps']);
  });

/* ------------------------------------------------------------------------- *
 * Apply-time contracts: blueprint + brand/product -> vibe coder build spec
 * ------------------------------------------------------------------------- */

/** Minimal product seed the target funnel is built around. */
export const BlueprintProductSeedSchema = z
  .object({
    productId: z.string().optional(),
    name: NonEmptyStringSchema,
    slug: z.string().optional(),
    url: z.string().url().optional(),
    price: z.number().nonnegative().optional(),
    currency: z.string().optional(),
    images: z.array(z.string()).default([]),
    variants: z.array(z.string()).default([]),
    description: z.string().optional(),
    category: z.string().optional(),
    brand: z.string().optional(),
    sku: z.string().optional(),
  })
  .strict();

/**
 * Input to the apply operation: which blueprint, plus the target brand context
 * (reusing the rich BrandPageContext model) and the product to build around.
 */
export const BlueprintApplyInputSchema = z
  .object({
    blueprintId: IdSchema,
    tenantId: IdSchema,
    storeId: z.string().optional(),
    brandContext: BrandPageContextSchema,
    product: BlueprintProductSeedSchema,
    tokenPolicy: BlueprintTokenPolicyEnum.optional(),
    viewports: z.array(BlueprintViewportEnum).min(1).optional(),
  })
  .strict();

export interface VibeCoderComponentSpec {
  id: string;
  blockType: z.infer<typeof BlockTypeEnum>;
  conversionIntent: z.infer<typeof ConversionIntentEnum>;
  nativeComponent?: z.infer<typeof BlueprintNativeComponentEnum> | undefined;
  order: number;
  props: Record<string, JsonValue>;
  layout?: z.infer<typeof PageBlockLayoutSchema> | undefined;
  styles?: Record<string, JsonValue> | undefined;
  advanced?: Record<string, JsonValue> | undefined;
  responsiveOverrides?: Record<string, JsonValue> | undefined;
  content: Record<string, JsonValue>;
  events?: z.infer<typeof KnownBlockEventsSchema> | undefined;
  children: VibeCoderComponentSpec[];
}

/**
 * A resolved component ready for the generator (slots bound to real values).
 * Its recursive fidelity fields match the Blueprint tree one-for-one.
 */
export const VibeCoderComponentSpecSchema: z.ZodType<
  VibeCoderComponentSpec,
  z.ZodTypeDef,
  unknown
> = z.lazy(() =>
    z
      .object({
        id: NonEmptyStringSchema,
        blockType: BlockTypeEnum,
        conversionIntent: ConversionIntentEnum,
        nativeComponent: BlueprintNativeComponentEnum.optional(),
        order: z.number().int().nonnegative(),
        props: z.record(JsonValueSchema).default({}),
        layout: PageBlockLayoutSchema.optional(),
        styles: z.record(JsonValueSchema).optional(),
        advanced: z.record(JsonValueSchema).optional(),
        responsiveOverrides: z.record(JsonValueSchema).optional(),
        content: z.record(NonEmptyStringSchema, JsonValueSchema).default({}),
        events: KnownBlockEventsSchema.optional(),
        children: z.array(VibeCoderComponentSpecSchema).default([]),
      })
      .strict(),
  );

/** One resolved page/route to build on the Storefront Layout. */
export const VibeCoderPageSpecSchema = z
  .object({
    id: NonEmptyStringSchema,
    position: z.number().int().nonnegative(),
    stepType: StepTypeEnum,
    experienceType: ExperienceTypeEnum,
    layoutMode: LayoutModeEnum.optional(),
    route: z.string().optional(),
    components: z.array(VibeCoderComponentSpecSchema).default([]),
    listeners: z.array(BlueprintStepListenerSchema),
    transitions: z.array(BlueprintStepTransitionSchema).default([]),
  })
  .strict();

/** A resolved asset binding (blueprint slot -> target brand asset). */
export const VibeCoderAssetBindingSchema = z
  .object({
    role: BlueprintAssetSlotRoleEnum,
    key: NonEmptyStringSchema,
    assetRef: NonEmptyStringSchema,
  })
  .strict();

/**
 * The build spec handed to the vibe coder. Fully resolved and brand-specific:
 * pages + components with content, resolved design tokens, asset bindings, and
 * the native apps to install/configure.
 */
export const VibeCoderBuildSpecSchema = z
  .object({
    blueprintId: IdSchema,
    targetSurface: z.literal('storefront-layout'),
    viewports: z.array(BlueprintViewportEnum).min(1),
    tokenPolicy: BlueprintTokenPolicyEnum,
    designTokens: BlueprintDesignSystemSchema.optional(),
    brand: z
      .object({
        brandName: NonEmptyStringSchema,
        logoAssetId: z.string().optional(),
      })
      .strict(),
    pages: z.array(VibeCoderPageSpecSchema).min(1),
    assetBindings: z.array(VibeCoderAssetBindingSchema).default([]),
    nativeAppsToConfigure: z.array(BlueprintNativeComponentEnum).default([]),
    prompt: z.string().optional(),
  })
  .strict()
  .superRefine((spec, ctx) => {
    addPortableStepGraphIssues(spec.pages, ctx, ['pages']);
  });

/* ------------------------------------------------------------------------- *
 * Validation helper (mirrors validatePageContent)
 * ------------------------------------------------------------------------- */

export interface BlueprintValidationResult {
  valid: boolean;
  errors: string[];
  data: FunnelBlueprint | null;
}

export function validateFunnelBlueprint(
  input: Record<string, unknown>,
): BlueprintValidationResult {
  const result = FunnelBlueprintSchema.safeParse(input);

  if (result.success) {
    return { valid: true, errors: [], data: result.data };
  }

  return {
    valid: false,
    errors: result.error.issues.map((issue) => {
      const path = issue.path.length > 0 ? `${issue.path.join('.')}: ` : '';
      return `${path}${issue.message}`;
    }),
    data: null,
  };
}

/* ------------------------------------------------------------------------- *
 * Inferred types
 * ------------------------------------------------------------------------- */

export type BlueprintStatus = (typeof BLUEPRINT_STATUSES)[number];
export type BlueprintSourceKind = (typeof BLUEPRINT_SOURCE_KINDS)[number];
export type BlueprintTokenPolicy = (typeof BLUEPRINT_TOKEN_POLICIES)[number];
export type BlueprintViewport = (typeof BLUEPRINT_VIEWPORTS)[number];
export type ConversionIntent = (typeof CONVERSION_INTENTS)[number];
export type BlueprintAssetSlotRole =
  (typeof BLUEPRINT_ASSET_SLOT_ROLES)[number];
export type BlueprintNativeComponent =
  (typeof BLUEPRINT_NATIVE_COMPONENTS)[number];

export type BlueprintDesignSystem = z.infer<typeof BlueprintDesignSystemSchema>;
export type BlueprintProvenance = z.infer<typeof BlueprintProvenanceSchema>;
export type BlueprintClassification = z.infer<
  typeof BlueprintClassificationSchema
>;
export type BlueprintContentSlot = z.infer<typeof BlueprintContentSlotSchema>;
export type BlueprintComponentEvidence = z.infer<
  typeof BlueprintComponentEvidenceSchema
>;
export type BlueprintStepTransition = z.infer<
  typeof BlueprintStepTransitionSchema
>;
export type BlueprintStepListener = z.infer<typeof BlueprintStepListenerSchema>;
export type BlueprintStep = z.infer<typeof BlueprintStepSchema>;
export type BlueprintAssetSlot = z.infer<typeof BlueprintAssetSlotSchema>;
export type BlueprintConversionPattern = z.infer<
  typeof BlueprintConversionPatternSchema
>;
export type BlueprintApplyContract = z.infer<
  typeof BlueprintApplyContractSchema
>;
export type FunnelBlueprint = z.infer<typeof FunnelBlueprintSchema>;
export type BlueprintProductSeed = z.infer<typeof BlueprintProductSeedSchema>;
export type BlueprintApplyInput = z.infer<typeof BlueprintApplyInputSchema>;
export type VibeCoderPageSpec = z.infer<typeof VibeCoderPageSpecSchema>;
export type VibeCoderAssetBinding = z.infer<typeof VibeCoderAssetBindingSchema>;
export type VibeCoderBuildSpec = z.infer<typeof VibeCoderBuildSpecSchema>;
